import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, downloadFile, num, fmtDate } from "@/lib/api";
import { Stepper } from "@/components/Stepper";
import { TransactionTable } from "@/components/TransactionTable";
import { AnalysisView } from "@/components/AnalysisView";
import { AuditTrail } from "@/components/AuditTrail";
import { ReuploadPages } from "@/components/ReuploadPages";
import { EodBalancePanel } from "@/components/EodBalancePanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Loader2, FileSpreadsheet, FileText, Files, AlertTriangle, Coins, FileArchive } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const RefundNotice = ({ n }) => n ? (
  <p className="mt-3 inline-flex items-center gap-2 rounded-md border border-green-200 bg-ok px-3 py-2 text-sm text-ok-foreground"
     data-testid="credits-refunded-notice">
    <Coins className="h-4 w-4" /> {n} OCR credit{n > 1 ? "s" : ""} returned to your account for the file{n > 1 ? "s" : ""} that could not be processed.
  </p>
) : null;

export default function Workspace() {
  const { jobId } = useParams();
  const { refresh } = useAuth();
  const [record, setRecord] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [auditKey, setAuditKey] = useState(0);
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/analysis/${jobId}`);
      setRecord(data);
      if (data.status !== "processing") {
        clearInterval(timer.current);
        setStep((s) => (s === 1 ? 2 : s));
        if (data.credits_refunded) refresh();
      }
      return data;
    } catch (e) {
      clearInterval(timer.current);
      toast.error(errMsg(e));
    }
  };

  const startPolling = () => {
    clearInterval(timer.current);
    load();
    timer.current = setInterval(load, 2000);
  };

  useEffect(() => {
    startPolling();
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const save = async (rows, reason) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/analysis/${jobId}/transactions`, { transactions: rows, reason: reason || null });
      setRecord((r) => ({ ...r, transactions: data.transactions, analysis: data.analysis }));
      setAuditKey((k) => k + 1);
      toast.success("Saved — analytics recalculated");
      setStep(4);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const bulkDuplicates = async (path, reason, label) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/analysis/${jobId}/duplicates/${path}`, { reason: reason || null });
      setRecord((r) => ({ ...r, transactions: data.transactions, analysis: data.analysis }));
      setAuditKey((k) => k + 1);
      toast.success(`${label}: ${data.excluded ?? data.restored} transaction(s) updated — analytics recalculated`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBulkBusy(false); }
  };

  if (!record)
    return <div className="flex h-64 items-center justify-center" data-testid="workspace-loading">
      <Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  if (record.status === "processing") {
    const elapsed = record.elapsed_sec ?? 0;
    const limit = record.time_limit_sec ?? 600;
    const mm = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    const slow = elapsed > 120;
    const pg = record.progress || {};
    const total = pg.pages_total || 0;
    const done = Math.min(pg.pages_done || 0, total);
    const stageLabel = { preparing: "Preparing pages", reading: "Reading pages", analyzing: "Analysing transactions" }[pg.stage]
      || "Starting";
    const pct = pg.stage === "analyzing" ? 100 : total ? Math.round((done / total) * 100) : 0;
    const multi = (pg.files_total || 0) > 1;
    return (
      <div className="max-w-3xl space-y-6" data-testid="workspace-processing">
        <Stepper current={1} />
        <div className="rounded-lg border border-border bg-white p-10 text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-primary" />
          <h2 className="mt-5 font-display text-xl font-semibold">Processing your document…</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Rasterising pages, preprocessing images, reconstructing the transaction table and normalising values.
            Pages are read in parallel; this usually takes under a minute.
          </p>

          <div className="mx-auto mt-6 max-w-md text-left" data-testid="processing-progress">
            <div className="mb-2 flex items-center justify-between text-xs">
              <span className="font-semibold text-foreground" data-testid="processing-stage">{stageLabel}</span>
              <span className="tabular-nums text-muted-foreground" data-testid="processing-page-count">
                {pg.stage === "reading" && total ? `Page ${done} of ${total}` :
                 pg.stage === "analyzing" ? "All pages read" :
                 total ? `${total} page${total > 1 ? "s" : ""}` : "…"}
              </span>
            </div>
            <Progress value={pct} className={pct === 0 ? "animate-pulse" : ""} data-testid="processing-progress-bar" />
            <p className="mt-2 truncate text-xs text-muted-foreground" data-testid="processing-current-file">
              {multi && pg.file_index ? `File ${pg.file_index} of ${pg.files_total} · ` : ""}
              {pg.current_file || (record.file_names || []).join(", ")}
            </p>
          </div>

          <p className="mt-4 text-xs font-medium tabular-nums text-muted-foreground" data-testid="processing-elapsed">
            Elapsed {mm(elapsed)} · stops automatically at {mm(limit)}
          </p>
          {slow && (
            <p className="mt-3 rounded-md border border-warn-border bg-warn px-3 py-2 text-xs text-warn-foreground"
               data-testid="processing-slow-hint">
              This is taking longer than usual. If the time limit is reached the job will stop and show an error
              here — you won't be left waiting.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (record.status === "failed")
    return (
      <div className="max-w-3xl space-y-6" data-testid="workspace-failed">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-red-800">
            <AlertTriangle className="h-5 w-5" /> {record.timed_out ? "Analysis timed out" : "Extraction unsuccessful"}
          </h2>
          {(record.warnings || []).map((w) => <p key={w} className="mt-2 text-sm text-red-700" data-testid="workspace-failed-reason">{w}</p>)}
          <p className="mt-3 text-sm text-red-700">
            Please upload a clearer image/PDF, or try a higher-resolution scan.
          </p>
          <RefundNotice n={record.credits_refunded} />
          <br />
          <Button asChild variant="outline" className="mt-4 rounded-full bg-white" data-testid="workspace-retry-btn">
            <Link to="/analyzer">Upload another statement</Link>
          </Button>
        </div>
      </div>
    );

  const st = record.statement || {};
  const s = record.analysis?.summary || {};

  return (
    <div className="space-y-6" data-testid="workspace-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Statement workspace</p>
          <h1 className="mt-2 font-display text-2xl font-extrabold tracking-tighter sm:text-3xl">
            {st.account_holder || "Statement analysis"}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="bg-secondary text-secondary-foreground">
              {st.bank_name === "Generic" ? "Bank format detected as Generic" : st.bank_name}
            </Badge>
            <span>Account {st.account_number_masked || "—"}</span>
            <span>· {fmtDate(s.statement_period_from)} – {fmtDate(s.statement_period_to)}</span>
            <span>· {num(record.transactions?.length)} transactions</span>
            {record.is_demo && <Badge className="bg-warn text-warn-foreground">DEMO DATA</Badge>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full bg-white"
                  onClick={() => downloadFile(`/export/excel/${jobId}`, "GrowRupee_Statement_Analysis.xlsx")
                    .then(() => { toast.success("Excel exported"); setStep(6); })
                    .catch((e) => toast.error(errMsg(e)))}
                  data-testid="export-excel-btn">
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Export Excel
          </Button>
          <Button className="rounded-full"
                  onClick={() => downloadFile(`/export/pdf/${jobId}`, "GrowRupee_Financial_Report.pdf")
                    .then(() => { toast.success("Report generated"); setStep(6); })
                    .catch((e) => toast.error(errMsg(e)))}
                  data-testid="generate-report-btn">
            <FileText className="mr-2 h-4 w-4" /> Generate Financial Analysis Report
          </Button>
          <Button variant="outline" className="rounded-full bg-white"
                  onClick={() => downloadFile(`/analysis/${jobId}/cam/export?fmt=xlsx&t=${Date.now()}`, "GrowRupee_CAM_Full_Report.xlsx")
                    .then(() => toast.success("Full CAM report downloaded"))
                    .catch((e) => toast.error(errMsg(e)))}
                  data-testid="download-full-report-btn">
            <FileArchive className="mr-2 h-4 w-4" /> Download Full Report
          </Button>
        </div>
      </div>

      <Stepper current={step} onStep={setStep} />

      <ReuploadPages jobId={jobId} pending={record.pending_pages} onStarted={startPolling} />

      {(record.warnings || []).length > 0 && (
        <div className="space-y-1 rounded-lg border border-warn-border bg-warn p-4 text-sm text-warn-foreground"
             data-testid="workspace-warnings">
          {record.warnings.map((w) => <p key={w}>{w}</p>)}
          <RefundNotice n={record.credits_refunded} />
        </div>
      )}

      <div className="rounded-lg border border-border bg-white p-5" data-testid="source-files">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold">
          <Files className="h-4 w-4 text-primary" /> Source documents
        </h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(record.files || []).map((f) => (
            <div key={f.filename} className="rounded-md border border-border p-3">
              <p className="truncate text-sm font-medium">{f.filename}</p>
              <p className="text-xs text-muted-foreground">
                {f.pages || 0} page(s) · {f.transactions || 0} txns · OCR {Math.round((f.confidence || 0) * 100)}%
              </p>
              <Badge variant="outline" className="mt-2">{f.status}</Badge>
            </div>
          ))}
        </div>
      </div>

      {step <= 4 ? (
        <div className="space-y-4">
          <div>
            <h2 className="font-display text-xl font-semibold">
              {step === 2 ? "Extracted Transactions" : step === 3 ? "Review & Correct" : "Validate"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Low-confidence cells are highlighted in yellow. Click any cell to correct it, then save to
              recalculate all analytics. Nothing is finalised until you review.
            </p>
          </div>
          <TransactionTable transactions={record.transactions || []} onSave={save} saving={saving}
                            bulkBusy={bulkBusy}
                            onBulkExcludeDuplicates={(reason) => bulkDuplicates("exclude-all", reason, "Duplicates excluded")}
                            onBulkRestore={(reason) => bulkDuplicates("restore-all", reason, "Transactions restored")} />
          <AuditTrail jobId={jobId} refreshKey={auditKey} />
          <div className="flex justify-end">
            <Button className="rounded-full" onClick={() => setStep(5)} data-testid="go-to-analysis-btn">
              Continue to Analysis
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <AnalysisView record={record} />
          <EodBalancePanel jobId={jobId} />
          <AuditTrail jobId={jobId} refreshKey={auditKey} />
          <div className="flex justify-start">
            <Button variant="outline" className="rounded-full bg-white" onClick={() => setStep(3)}
                    data-testid="back-to-review-btn">Back to review</Button>
          </div>
        </div>
      )}
    </div>
  );
}
